import { Link as GatsbyLink } from "gatsby"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import { convertToBgImage } from "gbimage-bridge"
import * as React from "react"
import {
  CheckCircle
} from "react-feather"
import { useForm, useFormContext } from 'react-hook-form'
import BackImage from "./BackImage"
import * as styles from "./ui.css"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSearch, faFilter } from "@fortawesome/free-solid-svg-icons"

const ABSOLUTE_URL_REGEX = /^[a-zA-Z][a-zA-Z\d+\-.]*?:/;

const WINDOWS_PATH_REGEX = /^[a-zA-Z]:\\/;

const isAbsoluteUrl = (url) => {
	if (typeof url !== 'string') {
		throw new TypeError(`Expected a \`string\`, got \`${typeof url}\``);
	}

	if (WINDOWS_PATH_REGEX.test(url)) {
		return false;
	}

	return ABSOLUTE_URL_REGEX.test(url);
}

export const cx = (...args) => args.filter(Boolean).join(" ")

export function Base({
  as: Component = "div",
  cx: _cx = [],
  className,
  iRef,
  ...props
}) {
  const comp = <Component className={cx(..._cx, className)} {...props} ref={iRef || undefined} />
  return comp;
}

export function Container({ width = "normal", cx, ...props }) {
  return <Base cx={[cx, styles.containers[width]]} {...props} />
}

export function Flex({
  variant,
  gap = 3,
  gutter,
  wrap,
  responsive,
  marginY,
  alignItems,
  cx: _cx = [],
  ...props
}) {
  return (
    <Base
      cx={[
        styles.flex,
        variant && styles.flexVariants[variant],
        responsive && styles.flexVariants.responsive,
        wrap && styles.flexVariants.wrap,
        gutter && styles.gutter[gutter],
        gutter ? styles.flexGap[0] : styles.flexGap[gap],
        marginY && styles.marginY[marginY],
        alignItems && styles.flexVariants[alignItems],
        ..._cx,
      ]}
      {...props}
    />
  )
}

export function Box({
  width = "full",
  background,
  padding,
  paddingY,
  radius,
  center = false,
  order,
  cx: _cx = [],
  ...props
}) {
  return (
    <Base
      cx={[
        styles.widths[width],
        background && styles.backgrounds[background],
        padding && styles.padding[padding],
        paddingY && styles.paddingY[paddingY],
        radius && styles.radii[radius],
        center && styles.box.center,
        order && styles.order[order],
        ..._cx,
      ]}
      {...props}
    />
  )
}

export function FlexList({ cx, ...props }) {
  return <Flex as="ul" cx={[cx, styles.list]} {...props} />
}

export function Space({ size = "auto", ...props }) {
  return <Base cx={[styles.margin[size]]} {...props} />
}

export function Nudge({ left, right, top, bottom, ...props }) {
  return (
    <Base
      cx={[
        left && styles.marginLeft[-left],
        right && styles.marginRight[-right],
        top && styles.marginTop[-top],
        bottom && styles.marginBottom[-bottom],
      ]}
      {...props}
    />
  )
}

export function Section(props) {
  return <Box as="section" className={styles.section} {...props} />
}

export const BackgroundSection = ({ children, image, ...props }) => {
  return (
    <BackImage
      Tag="section"
      backgroundImage={image.gatsbyImageData.images.fallback.src}
      backgroundColor={"#CCE9F7"}
      {...props}
    >
      {children}
    </BackImage>
  )
}

export function Text({
  variant = "body",
  center = false,
  bold = false,
  ...props
}) {
  return (
    <Base
      cx={[
        styles.text[variant],
        center && styles.text.center,
        bold && styles.text.bold,
      ]}
      {...props}
    />
  )
}

export function Heading({ ...props }) {
  return <Text as="h2" variant="heading" {...props} />
}

export function Subhead({ ...props }) {
  return <Text as="h3" variant="subhead" {...props} />
}

export function HeroBody({ ...props }) {
  return <Text as="p" variant={props.variant || "herobody"} {...props} />
}

export function FeatureText({ ...props }) {
  return <Text as="p" variant="featuretext" {...props} />
}

export function Kicker({ ...props }) {
  return <Text variant="kicker" {...props} />
}

export function Link({ to, href, ...props }) {
  const url = href || to || ""
  if (isAbsoluteUrl(url)) {
    return (
      // eslint-disable-next-line jsx-a11y/anchor-has-content
      <a href={url} className={styles.link} {...props} />
    )
  }
  return <GatsbyLink to={url} className={styles.link} {...props} />
}

export const Success = () => {
  return (
    <Flex cx={[styles.successMessageBox]}>
      <Heading variant={"secondaryHeading"}>
        {'Formulário enviado'}
      </Heading>
      <CheckCircle style={{ color: '#0078B2', width: 80, height: 80, margin: 0, marginBottom: 30 }} />
      <Text>
        {'Em breve entraremos em contato pelo seu e-mail.'}
      </Text>
    </Flex>
  )
}

export function HeroForm({ children, buttonText, filled, loading, disabled, changeSubmit, methods, ...props }) {
  return (
      <form onSubmit={changeSubmit}>
        {children}
      </form>
  )
}

export function NavLink({ ...props }) {
  return <Base as={Link} cx={[styles.navlink]} {...props} />
}

function mailValidation(mail) {
  return import("company-email-validator").then((MailValidate) => {
    return MailValidate.isCompanyEmail(mail);
  });
}

const SearchIcon = () => {
  return (
    <Base as="div" style={{paddingTop: 12, paddingLeft: 16, paddingRight: 8}}>
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
       <circle cx="7.22222" cy="7.22222" r="6.22222" stroke="#762BED" stroke-linecap="round" stroke-linejoin="round"/>
       <path d="M14.9995 15L11.6162 11.6167" stroke="#762BED" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </Base>
  )
}

const FilterIcon = () => {
  return (
    <Base as="div" style={{paddingTop: 12, paddingLeft: 16, paddingRight: 8}}>
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14.6663 2H1.33301L6.66634 8.30667V12.6667L9.33301 14V8.30667L14.6663 2Z" stroke="#762BED" stroke-width="0.666667" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </Base>
  )
}

export const Input = ({ type, register, getFieldState, formState: {errors}, name, placeholder, validate, component, required, min, filter, search, max, onChange, ...props }) => {
  const registerData = {
    ...(required ? { required: 'Deve estar preenchido' } : {}),
    ...(min ? { minLength: { value: min, message: 'Deve ser maior' } } : {}),
    ...(max ? { maxLength: { value: max, message: 'Deve ser menor' } } : {}),
    ...((validate === 'phone') ? { pattern: { value: /^\+[0-9]{2}\s?[0-9]{2}\s?(([0-9]{4}\s?[0-9]{4})|([0-9]{5}\s?[0-9]{3,4}))$/i, message: 'Telefone inválido' } } : {}),
    ...((validate === 'company_mail') ? { validate: mailValidation } : {})
  };
  const { ref, ...nameRegister } = register(name, registerData);

  const { error } = getFieldState(name);

  const invalid = error !== undefined;
  
  let comp = <Base
        as={component || 'input'} cx={[styles.inputForm]} type={type}
        id={name} name={name} placeholder={placeholder}
        {...props}
        required={required}
        {...nameRegister}
        iRef={ref}
        onChange={(e) => { onChange && onChange({name, value: e.target.value}) }}
        aria-invalid={invalid}
      />;

  if (validate === 'phone') {
    comp = <Base
          as={component || 'input'} cx={[styles.inputForm]} type={type}
          id={name} placeholder={placeholder}
          {...props}
          iRef={ref}
          required={required}
          onBlur={(e) => nameRegister.onBlur(e)}
          onChange={(e) => { nameRegister.onChange({ target: { name: name, value: e } }); onChange && onChange({name,value: e.target.value}) }}
          name={nameRegister.name}
          aria-invalid={invalid}
          />;
  }

  return (
    <Base as="div" cx={[styles.inputFormContainer]} >
      {search && <SearchIcon /> }
      {filter && <FilterIcon /> }
      {comp}
    </Base>
  );
}

export const Label = ({ name, fieldText, ...props }) => {
  return (
    <Base as={'label'} htmlFor={name} cx={[styles.labelForm]} {...props}>
      {fieldText}
    </Base>
  )
}

export const Errors = ({ name }) => {
  const { getFieldState, formState: { errors } } = useFormContext();
    if (false) console.log(errors);

  const { error } = getFieldState(name);
  if (!error) return null;
  if (error.type === 'required') return null;
  let message = error.message;
  if (error.type === 'validate') message = 'Não é um e-mail corporativo válido.'
  return (
    <p style={{position: 'absolute', bottom: -5, color: '#FF6666', fontSize: 12}}>
      {message}
    </p>
  )
}

export const Field = ({
  name, type, placeholder, fieldText, component, children, required, defaultValue, min, max, validate
}) => {
  return (
    <div style={{position: 'relative'}}>
      <Label {...{ name, fieldText }} />
      <Input {...{ name, type, required, placeholder, component, children, defaultValue, validate, min, max }} />
      <Errors {...{ name }} />
    </div>
  )
}

export function Button({ variant = "primary", cx, ...props }) {
  return <Base as={Link} cx={[cx, styles.buttons[variant]]} {...props} />
}

export function NoLinkButton({ variant = "primary", cx, ...props }) {
  return <Base as={"a"} cx={[cx, styles.buttons[variant]]} {...props} />
}

export function SubmitButton({ variant = "primary_full", loading, value, disabled, ...props }) {
  return <Base
    as="input" cx={[styles.buttons[variant]]}
    disabled={disabled} type='submit'
    value={loading ? 'Enviando ...' : value} {...props} />
}


export function ButtonList({ links = [], reversed = false, ...props }) {
  const getVariant = (i) => {
    if (reversed) {
      return i === 0 ? "reversed" : "linkReversed"
    }
    return i === 0 ? "primary" : "link"
  }
  return (
    <FlexList marginY={4} {...props}>
      {links &&
        links.map((link, i) => (
          <li key={link.id}>
            <Button to={'#heroForm'} href={link.href} variant={getVariant(i)}>
              {link.text}
            </Button>
          </li>
        ))}
    </FlexList>
  )
}

export function CTALink(props) {
  return <Base as={Link} cx={[styles.ctaLink]} {...props} />
}

export function LinkList({ links = [], ...props }) {
  return (
    <FlexList {...props}>
      {links &&
        links.map((link, i) => (
          <li key={link.id}>
            <CTALink href={link.href}>{link.text}</CTALink>
          </li>
        ))}
    </FlexList>
  )
}

export function Blockquote(props) {
  return <Base as="blockquote" cx={[styles.blockquote]} {...props} />
}

export function Logo({ alt, image, size = "small" }) {
  return (
    <GatsbyImage
      alt={alt}
      image={getImage(image)}
      className={styles.logos[size]}
    />
  )
}

export function Brand({ alt, image, size = "small" }) {
  return (
    <GatsbyImage
      alt={alt}
      image={getImage(image)}
      className={styles.brands[size]}
    />
  )
}

export function Icon({ alt, image, size = "medium" }) {
  return (
    <GatsbyImage
      alt={alt}
      image={getImage(image)}
      className={styles.icons[size]}
    />
  )
}

export function IconLink(props) {
  return <NavLink cx={[styles.iconLink]} {...props} />
}

export function InteractiveIcon(props) {
  return <Base as="button" cx={[styles.interactiveIcon]} {...props} />
}

export function VisuallyHidden(props) {
  return <Base as="span" cx={[styles.visuallyHidden]} {...props} />
}

export function BlockLink(props) {
  return <Link className={styles.blockLink} {...props} />
}

export const Card = (props) => {
  return (
    <Box cx={[styles.cardBox]} >
      {props.children}
    </Box>
  )
}